import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import {
  Wallet, Plus, Edit2, Save, X, AlertCircle, Trash2,
  TrendingUp, TrendingDown, Minus, ChevronDown, ChevronRight, Banknote, Flame, Clock, Scale,
  BadgeCheck, Sparkles, Info,
} from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardValue, CardSubtitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { api } from '@/lib/api';
import { formatNumber } from '@/lib/utils';
import { construirSerieDoBalanco } from '@/lib/financasSerie';

// ─── Tipos ────────────────────────────────────────────────────────────────────

// Padrão B+ estruturado (Monet 18/08): campos extras opcionais além do mínimo
// desc+cents. Compat retroativa total — rows antigas com só desc+cents continuam
// renderizando normal. Modal mostra APENAS campos presentes (sem "N/A" pra
// faltantes). Badge de categoria só aparece se `category` estiver set.
type Category   = 'operacional' | 'infra' | 'juridico' | 'marketing' | 'contabilidade' | 'equipamento' | 'outros';
type Recurrence = 'mensal' | 'bimestral' | 'trimestral' | 'semestral' | 'anual' | 'bienal' | 'unico';

interface OtherItem {
  desc:         string;
  cents:        number;
  vendor?:      string;
  category?:    Category;
  source?:      string;
  invoice_ref?: string;
  recurrence?:  Recurrence;
  next_charge?: string;   // YYYY-MM
}

// Receita conferida à mão — o que a loja REPORTOU, não o que o webhook estimou.
// `source` é obrigatório: número de receita sem origem declarada não é
// auditável, e auditar bruto → líquido → depositado é o motivo do campo existir.
interface RevenueItem {
  source:         string;   // apple_financereports | google_playconsole | extrato_bancario_manual
  gross_cents:    number;
  net_cents?:     number;
  takehome_pct?:  number;
  product?:       string;
  unit?:          number;
  period_fiscal?: string;   // o ano fiscal da Apple começa em outubro: "2026-11" é agosto/2026
  note?:          string;
}

// Dinheiro que o Pablo põe na PJ. NÃO é receita: entra no caixa, mas fica fora
// de burn e não alarga runway. `kind` separa o que virou patrimônio da empresa
// do que ele pretende reaver — distinção que o contador precisa no fechamento.
interface CapitalItem {
  source:   string;
  cents:    number;
  kind:     'aporte_permanente' | 'reembolso_pendente';
  date?:    string;
  purpose?: string;
}

interface Observation {
  type:            string;
  note?:           string;
  banco?:          string;
  value_cents?:    number;
  variacao_cents?: number;
  date?:           string;
}

interface FinanceRow {
  // NULL quando o mês tem receita mas nenhum custo lançado ainda: a linha de
  // company_finances só nasce no primeiro salvamento. Por isso a identidade da
  // linha na tabela é o MÊS, não o id — ver `rowKey`.
  id:                            number | null;
  month:                         string;     // YYYY-MM-DD (primeiro dia)
  cash_balance_cents:            number;     // snapshot manual — fonte de verdade quando confirmed_at != null
  cash_balance_confirmed_at:     string | null;  // ISO timestamp quando user confirmou (bateu extrato)
  cash_balance_projected_cents:  number;     // calculado no backend: cascade desde último confirmado somando revenue - custos
  railway_cost_cents:            number;
  r2_cost_cents:                 number;
  resend_cost_cents:             number;
  posthog_cost_cents:            number;
  other_cost_cents:              number;       // soma agregada (calculada do breakdown se houver)
  other_breakdown:               OtherItem[];  // itens individuais com descrição
  total_cost_cents:              number;
  // DERIVADO — somado das cobranças registradas em pro_revenue_events.
  revenue_cents:                 number;
  revenue_derived_gross_cents:   number;
  // true quando TODAS as cobranças do mês vieram do relatório oficial da loja.
  // Receita derivada não é sinônimo de estimada: o backfill trouxe julho e
  // agosto com o líquido real medido.
  revenue_derived_is_measured:   boolean;
  // MEDIDO — conferido no relatório da loja e no extrato. Convive com o
  // derivado de propósito: o delta entre os dois É a informação. Com as duas
  // fontes lado a lado desde julho, a estimativa errada de 74% do RevenueCat
  // teria saltado à vista em vez de custar dois dias de investigação.
  revenue_gross_cents:           number | null;
  revenue_net_cents:             number | null;
  revenue_breakdown:             RevenueItem[];
  // Qual dos dois o backend usou na projeção de caixa (medido ganha).
  revenue_effective_cents:       number;
  net_burn_cents:                number;       // custo − receita, SEM aporte
  capital_in_cents:              number | null;
  capital_in_breakdown:          CapitalItem[];
  observations:                  Observation[];
  notes:                         string | null;       // legacy — string única, mantida por retrocompatibilidade
  notes_breakdown:               string[];            // array de notas (uso novo do frontend)
  created_at:                    string;
  updated_at:                    string;
}

type BalancePeriod = 'current_month' | 'last_3m' | 'last_12m' | 'ytd' | 'all';

interface BalanceCalc {
  revenueCents:    number;
  costCents:       number;
  balanceCents:    number;
  comparisonPct:   number | null;   // % vs período anterior equivalente (null se sem dados)
  rangeLabel:      string;
  monthsIncluded:  number;
}

interface OtherFormItem {
  desc: string;
  brl:  string;
}

// Receita e aporte no formulário. Valores em texto, como o resto do form —
// a conversão para centavos acontece só no envio.
interface RevenueFormItem {
  source:      string;
  gross_brl:   string;
  net_brl:     string;
  product:     string;
  period_fiscal: string;
}

interface CapitalFormItem {
  source:  string;
  brl:     string;
  kind:    'aporte_permanente' | 'reembolso_pendente';
  purpose: string;
}

interface FormState {
  month:              string;
  cash_balance_brl:   string;
  railway_cost_brl:   string;
  r2_cost_brl:        string;
  resend_cost_brl:    string;
  posthog_cost_brl:   string;
  other_items:        OtherFormItem[];
  notes_items:        string[];    // lista dinâmica de notas; estrutura espelha `other_items`
  revenue_items:      RevenueFormItem[];
  capital_items:      CapitalFormItem[];
}

// As origens que existem hoje. Texto livre seria pior: "apple", "Apple",
// "App Store" e "apple_financereports" viram quatro origens diferentes numa
// agregação, e aí o campo deixa de servir para auditar.
const REVENUE_SOURCES = [
  { value: 'apple_financereports',   label: 'Apple — relatório financeiro' },
  { value: 'google_playconsole',     label: 'Google Play Console' },
  { value: 'extrato_bancario_manual', label: 'Extrato bancário' },
] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const BRL = (cents: number) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Compacto pra ticks do gráfico (sem decimais a não ser pra > 1k).
// Crítico que caiba em ~50px de largura — Recharts não rebatiza ticks que estouram.
const BRL_SHORT = (cents: number) => {
  const v = cents / 100;
  if (Math.abs(v) >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000)     return `R$ ${(v / 1_000).toFixed(1)}k`;
  if (v === Math.floor(v))      return `R$ ${v.toFixed(0)}`;
  return `R$ ${v.toFixed(0)}`;
};

const monthLabel = (iso: string) => {
  const [y, m] = iso.split('-');
  const names = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  return `${names[parseInt(m, 10) - 1]}/${y}`;
};

const currentMonthFirstDay = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const emptyForm = (month?: string): FormState => ({
  month:            month || currentMonthFirstDay(),
  cash_balance_brl: '',
  railway_cost_brl: '',
  r2_cost_brl:      '',
  resend_cost_brl:  '',
  posthog_cost_brl: '',
  other_items:      [],
  notes_items:      [],
  revenue_items:    [],
  capital_items:    [],
});

const fromRow = (r: FinanceRow): FormState => {
  const breakdown = r.other_breakdown && r.other_breakdown.length > 0
    ? r.other_breakdown.map((it) => ({ desc: it.desc || '', brl: (it.cents / 100).toFixed(2) }))
    : (r.other_cost_cents > 0
        ? [{ desc: '', brl: (r.other_cost_cents / 100).toFixed(2) }]
        : []);

  // notes_breakdown é fonte primária; cai pra `notes` legacy se array vier vazio
  const notes = r.notes_breakdown && r.notes_breakdown.length > 0
    ? [...r.notes_breakdown]
    : (r.notes ? [r.notes] : []);

  return {
    month:            r.month.slice(0, 10),
    // Vazio quando o mês não foi confirmado contra o extrato. Mostrar "0.00"
    // afirma que o saldo é zero, quando o que se quer dizer é "não informado" —
    // e a coluna da tabela está exibindo o valor calculado, não zero. Confundiu
    // de verdade em 10/09.
    cash_balance_brl: r.cash_balance_confirmed_at
      ? (r.cash_balance_cents / 100).toFixed(2)
      : '',
    railway_cost_brl: (r.railway_cost_cents / 100).toFixed(2),
    r2_cost_brl:      (r.r2_cost_cents      / 100).toFixed(2),
    resend_cost_brl:  (r.resend_cost_cents  / 100).toFixed(2),
    posthog_cost_brl: (r.posthog_cost_cents / 100).toFixed(2),
    other_items:      breakdown,
    notes_items:      notes,
    revenue_items:    (r.revenue_breakdown ?? []).map((it) => ({
      source:        it.source,
      gross_brl:     (it.gross_cents / 100).toFixed(2),
      net_brl:       it.net_cents != null ? (it.net_cents / 100).toFixed(2) : '',
      product:       it.product ?? '',
      period_fiscal: it.period_fiscal ?? '',
    })),
    capital_items:    (r.capital_in_breakdown ?? []).map((it) => ({
      source:  it.source,
      brl:     (it.cents / 100).toFixed(2),
      kind:    it.kind,
      purpose: it.purpose ?? '',
    })),
  };
};

// Identidade da linha na tabela. Não pode ser `id`: um mês que só tem receita
// vem com id null, e aí várias linhas compartilhariam a mesma chave — expandir
// uma abriria todas. O mês é único por construção (o banco tem UNIQUE + CHECK
// de dia 1).
const rowKey = (r: FinanceRow) => r.month.slice(0, 10);

const brlToCents = (brl: string): number => {
  const n = parseFloat(brl.replace(',', '.'));
  if (isNaN(n) || n < 0) return 0;
  return Math.round(n * 100);
};

// ─── Componente principal ────────────────────────────────────────────────────

// Lista vazia estável — ver o comentário no uso, dentro de `Financas`.
const SEM_LINHAS: never[] = [];

export function Financas() {
  const qc = useQueryClient();
  const [form, setForm]                   = useState<FormState | null>(null);
  const [editingRowId, setEditingRowId]   = useState<string | null>(null);   // null = modo "adicionar mês" (form aparece no topo); mês = modo "editar" (form inline na row)
  const [expandedId, setExpandedId]       = useState<string | null>(null);
  const [balancePeriod, setBalancePeriod] = useState<BalancePeriod>('current_month');
  // Modal detalhe item (Monet 24/08 sprint mínima): clicar item da lista abre
  // modal read-only com todos os campos extras (vendor, category, source,
  // invoice_ref, recurrence, next_charge). Editor rico é sprint desejável.
  const [detailItem, setDetailItem]       = useState<OtherItem | null>(null);

  // Reset combinado: usado tanto em "Cancelar" do form quanto em onSuccess da mutation.
  const closeForm = () => {
    setForm(null);
    setEditingRowId(null);
  };

  // Abrir form em modo "editar" para uma row existente (Mudança 2: inline abaixo).
  const startEditRow = (r: FinanceRow) => {
    setEditingRowId(rowKey(r));
    setExpandedId(rowKey(r));     // garante que a row está expandida, pra o form aparecer
    setForm(fromRow(r));
  };

  // Abrir form em modo "adicionar mês" (form aparece no topo, como antes).
  const startAddMonth = () => {
    setEditingRowId(null);
    setForm(emptyForm());
  };

  // Render do formulário — usado em 2 posições: no topo da página (modo adicionar)
  // e dentro da row expandida da tabela (modo editar inline). Guard para `form` null
  // mantém o TS feliz e a chamada é segura porque os call-sites verificam `form` antes.
  const renderForm = () => {
    if (!form) return null;
    const f = form;  // alias narrowed para o TS dentro dos closures
    return (
      <Card className="mb-4 border-brand">
        <CardHeader>
          <CardTitle>
            {rows.some((r) => r.month.startsWith(f.month.slice(0, 7))) ? 'Editar' : 'Adicionar'} — {monthLabel(f.month)}
          </CardTitle>
          <button onClick={closeForm} className="text-text-muted hover:text-text-primary">
            <X size={18} />
          </button>
        </CardHeader>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Mês (1º dia)" hint="formato YYYY-MM-01">
            <Input type="date" value={f.month} onChange={(e) => setForm({ ...f, month: e.target.value })} />
          </Field>

          <Field
            label="Caixa atual (R$)"
            hint="deixe vazio para o painel calcular; preencha só ao conferir o extrato"
          >
            <Input type="text" inputMode="decimal"
                   placeholder="calculado automaticamente"
                   value={f.cash_balance_brl}
                   onChange={(e) => setForm({ ...f, cash_balance_brl: e.target.value })} />
          </Field>

          <Field label="Railway (R$)">
            <Input type="text" inputMode="decimal" placeholder="ex: 145.00"
                   value={f.railway_cost_brl}
                   onChange={(e) => setForm({ ...f, railway_cost_brl: e.target.value })} />
          </Field>

          <Field label="Cloudflare R2 (R$)">
            <Input type="text" inputMode="decimal" placeholder="ex: 50.00"
                   value={f.r2_cost_brl}
                   onChange={(e) => setForm({ ...f, r2_cost_brl: e.target.value })} />
          </Field>

          <Field label="Resend (R$)">
            <Input type="text" inputMode="decimal" placeholder="0"
                   value={f.resend_cost_brl}
                   onChange={(e) => setForm({ ...f, resend_cost_brl: e.target.value })} />
          </Field>

          <Field label="PostHog (R$)">
            <Input type="text" inputMode="decimal" placeholder="0"
                   value={f.posthog_cost_brl}
                   onChange={(e) => setForm({ ...f, posthog_cost_brl: e.target.value })} />
          </Field>
        </div>

        {/* Bloco Outros — lista dinâmica */}
        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between">
            <label className="text-xs text-text-secondary">
              Outros custos <span className="ml-2 text-text-muted">· domínios, ferramentas, assinaturas — 1 item por linha</span>
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setForm({ ...f, other_items: [...f.other_items, { desc: '', brl: '' }] })}
            >
              <Plus size={12} /> Adicionar item
            </Button>
          </div>

          {f.other_items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-bg-input/40 py-3 text-center text-xs text-text-muted">
              Nenhum item. Clica em "Adicionar item" pra começar.
            </div>
          ) : (
            <div className="space-y-2">
              {f.other_items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    type="text"
                    placeholder="descrição (ex: Domínio amuzze.com.br)"
                    value={item.desc}
                    onChange={(e) => {
                      const next = [...f.other_items];
                      next[idx] = { ...next[idx], desc: e.target.value };
                      setForm({ ...f, other_items: next });
                    }}
                    className="flex-1"
                  />
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="R$ 0,00"
                    value={item.brl}
                    onChange={(e) => {
                      const next = [...f.other_items];
                      next[idx] = { ...next[idx], brl: e.target.value };
                      setForm({ ...f, other_items: next });
                    }}
                    className="w-32"
                  />
                  <button
                    onClick={() => {
                      const next = f.other_items.filter((_, i) => i !== idx);
                      setForm({ ...f, other_items: next });
                    }}
                    className="text-text-muted hover:text-red-400"
                    title="Remover item"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}

              <div className="mt-2 flex items-center justify-end text-xs text-text-secondary">
                Total Outros:
                <span className="ml-2 font-semibold text-brand">
                  {BRL(f.other_items.reduce((acc, it) => acc + brlToCents(it.brl), 0))}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* ─── Bloco Receita ───────────────────────────────────────────────
            O que a loja REPORTOU, conferido no extrato — separado do que o
            webhook estimou. As duas fontes convivem: o delta entre elas conta
            uma história (câmbio, arredondamento, webhook quebrado). */}
        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between">
            <label className="text-xs text-text-secondary">
              Receita conferida
              <span className="ml-2 text-text-muted">· valor do relatório da loja — o painel já mostra o estimado à parte</span>
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setForm({ ...f, revenue_items: [...f.revenue_items, {
                source: 'apple_financereports', gross_brl: '', net_brl: '', product: '', period_fiscal: '',
              }] })}
            >
              <Plus size={12} /> Adicionar receita
            </Button>
          </div>

          {f.revenue_items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-bg-input/40 py-3 text-center text-xs text-text-muted">
              Nenhuma receita lançada. O painel segue mostrando o valor estimado pelas cobranças.
            </div>
          ) : (
            <div className="space-y-2">
              {f.revenue_items.map((item, idx) => {
                const troca = (campo: keyof RevenueFormItem, valor: string) => {
                  const next = [...f.revenue_items];
                  next[idx] = { ...next[idx], [campo]: valor };
                  setForm({ ...f, revenue_items: next });
                };
                return (
                  <div key={idx} className="flex flex-wrap items-center gap-2">
                    <select
                      value={item.source}
                      onChange={(e) => troca('source', e.target.value)}
                      className="h-9 rounded-lg border border-border bg-bg-input px-2 text-xs text-text-primary"
                    >
                      {REVENUE_SOURCES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                    <Input
                      type="text" inputMode="decimal" placeholder="bruto R$"
                      value={item.gross_brl}
                      onChange={(e) => troca('gross_brl', e.target.value)}
                      className="w-28"
                    />
                    <Input
                      type="text" inputMode="decimal" placeholder="líquido R$"
                      value={item.net_brl}
                      onChange={(e) => troca('net_brl', e.target.value)}
                      className="w-28"
                    />
                    <Input
                      type="text" placeholder="produto"
                      value={item.product}
                      onChange={(e) => troca('product', e.target.value)}
                      className="flex-1 min-w-[120px]"
                    />
                    <button
                      onClick={() => setForm({ ...f, revenue_items: f.revenue_items.filter((_, i) => i !== idx) })}
                      className="text-text-muted hover:text-red-400"
                      title="Remover receita"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}

              <div className="mt-2 flex items-center justify-end gap-4 text-xs text-text-secondary">
                <span>
                  Bruto:
                  <span className="ml-1 font-semibold text-text-primary">
                    {BRL(f.revenue_items.reduce((acc, it) => acc + brlToCents(it.gross_brl), 0))}
                  </span>
                </span>
                <span>
                  Líquido:
                  <span className="ml-1 font-semibold text-emerald-400">
                    {BRL(f.revenue_items.reduce((acc, it) => acc + brlToCents(it.net_brl), 0))}
                  </span>
                </span>
              </div>
            </div>
          )}
        </div>

        {/* ─── Bloco Aporte ────────────────────────────────────────────────
            Dinheiro do Pablo entrando na PJ. Fica FORA de burn e runway: um mês
            com R$ 2.000 depositados não é um mês perto do break-even. */}
        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between">
            <label className="text-xs text-text-secondary">
              Aporte de sócio
              <span className="ml-2 text-text-muted">· entra no caixa, não conta como receita nem melhora o runway</span>
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setForm({ ...f, capital_items: [...f.capital_items, {
                source: '', brl: '', kind: 'aporte_permanente', purpose: '',
              }] })}
            >
              <Plus size={12} /> Adicionar aporte
            </Button>
          </div>

          {f.capital_items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-bg-input/40 py-3 text-center text-xs text-text-muted">
              Nenhum aporte lançado.
            </div>
          ) : (
            <div className="space-y-2">
              {f.capital_items.map((item, idx) => {
                const troca = (campo: keyof CapitalFormItem, valor: string) => {
                  const next = [...f.capital_items];
                  next[idx] = { ...next[idx], [campo]: valor } as CapitalFormItem;
                  setForm({ ...f, capital_items: next });
                };
                return (
                  <div key={idx} className="flex flex-wrap items-center gap-2">
                    <Input
                      type="text" placeholder="origem (ex: Pix Pablo PF→PJ)"
                      value={item.source}
                      onChange={(e) => troca('source', e.target.value)}
                      className="flex-1 min-w-[160px]"
                    />
                    <select
                      value={item.kind}
                      onChange={(e) => troca('kind', e.target.value)}
                      className="h-9 rounded-lg border border-border bg-bg-input px-2 text-xs text-text-primary"
                      title="Permanente vira patrimônio da PJ; pendente o Pablo pretende reaver"
                    >
                      <option value="aporte_permanente">Permanente</option>
                      <option value="reembolso_pendente">A reembolsar</option>
                    </select>
                    <Input
                      type="text" inputMode="decimal" placeholder="R$ 0,00"
                      value={item.brl}
                      onChange={(e) => troca('brl', e.target.value)}
                      className="w-32"
                    />
                    <button
                      onClick={() => setForm({ ...f, capital_items: f.capital_items.filter((_, i) => i !== idx) })}
                      className="text-text-muted hover:text-red-400"
                      title="Remover aporte"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}

              <div className="mt-2 flex items-center justify-end text-xs text-text-secondary">
                Total aportado:
                <span className="ml-2 font-semibold text-sky-400">
                  {BRL(f.capital_items.reduce((acc, it) => acc + brlToCents(it.brl), 0))}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Bloco Notas — lista dinâmica (espelha estrutura de Outros, sem campo valor) */}
        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between">
            <label className="text-xs text-text-secondary">
              Notas <span className="ml-2 text-text-muted">· contexto do mês, eventos, ajustes — uma observação por linha</span>
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setForm({ ...f, notes_items: [...f.notes_items, ''] })}
            >
              <Plus size={12} /> Adicionar nota
            </Button>
          </div>

          {f.notes_items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-bg-input/40 py-3 text-center text-xs text-text-muted">
              Nenhuma nota. Clica em "Adicionar nota" pra registrar uma observação.
            </div>
          ) : (
            <div className="space-y-2">
              {f.notes_items.map((text, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    type="text"
                    placeholder="ex: primeiro mês com Pro · Founding #1 entrou · ajuste R2"
                    value={text}
                    onChange={(e) => {
                      const next = [...f.notes_items];
                      next[idx] = e.target.value;
                      setForm({ ...f, notes_items: next });
                    }}
                    className="flex-1"
                  />
                  <button
                    onClick={() => {
                      const next = f.notes_items.filter((_, i) => i !== idx);
                      setForm({ ...f, notes_items: next });
                    }}
                    className="text-text-muted hover:text-red-400"
                    title="Remover nota"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={closeForm}>Cancelar</Button>
          <Button onClick={() => upsert.mutate(f)} disabled={upsert.isPending}>
            <Save size={14} /> {upsert.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>

        {upsert.isError && (
          <div className="mt-3 rounded bg-red-950/40 px-3 py-2 text-xs text-red-300">
            Erro ao salvar — verifique os campos e tente novamente.
          </div>
        )}
      </Card>
    );
  };

  const list = useQuery<{ data: FinanceRow[] }>({
    queryKey: ['finances-list'],
    queryFn: async () => (await api.get('/admin/finances')).data,
  });

  const upsert = useMutation({
    mutationFn: async (f: FormState) => {
      const otherBreakdown = f.other_items
        .map((it) => ({ desc: it.desc.trim(), cents: brlToCents(it.brl) }))
        .filter((it) => it.cents > 0);
      const notesBreakdown = f.notes_items
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

      // Só sobe item com bruto preenchido. Linha em branco recém-adicionada
      // seria recusada pelo backend (`source` e `gross_cents` são obrigatórios)
      // e derrubaria o salvamento inteiro do mês.
      const revenueBreakdown = f.revenue_items
        .filter((it) => brlToCents(it.gross_brl) > 0)
        .map((it) => ({
          source:      it.source,
          gross_cents: brlToCents(it.gross_brl),
          // Omitir e não mandar 0: o backend só soma o líquido dos itens quando
          // TODOS o declaram, e um 0 falso faria a soma sair a menos sem avisar.
          ...(brlToCents(it.net_brl) > 0 ? { net_cents: brlToCents(it.net_brl) } : {}),
          ...(it.product.trim()       ? { product: it.product.trim() } : {}),
          ...(it.period_fiscal.trim() ? { period_fiscal: it.period_fiscal.trim() } : {}),
        }));

      const capitalBreakdown = f.capital_items
        .filter((it) => brlToCents(it.brl) > 0 && it.source.trim().length > 0)
        .map((it) => ({
          source: it.source.trim(),
          cents:  brlToCents(it.brl),
          kind:   it.kind,
          ...(it.purpose.trim() ? { purpose: it.purpose.trim() } : {}),
        }));

      return api.post('/admin/finances', {
        month:              f.month,
        cash_balance_cents: brlToCents(f.cash_balance_brl),
        railway_cost_cents: brlToCents(f.railway_cost_brl),
        r2_cost_cents:      brlToCents(f.r2_cost_brl),
        resend_cost_cents:  brlToCents(f.resend_cost_brl),
        posthog_cost_cents: brlToCents(f.posthog_cost_brl),
        other_breakdown:      otherBreakdown,
        notes_breakdown:      notesBreakdown,
        notes:                null,  // legacy — null fixo; fonte de verdade é notes_breakdown
        revenue_breakdown:    revenueBreakdown,
        capital_in_breakdown: capitalBreakdown,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finances-list'] });
      qc.invalidateQueries({ queryKey: ['monet-kpis'] });
      qc.invalidateQueries({ queryKey: ['monet-historico'] });
      closeForm();
    },
  });

  const remove = useMutation({
    mutationFn: async (month: string) => api.delete(`/admin/finances/${month}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finances-list'] });
      qc.invalidateQueries({ queryKey: ['monet-kpis'] });
      qc.invalidateQueries({ queryKey: ['monet-historico'] });
    },
  });

  // ⚠️ `SEM_LINHAS` é constante de módulo, e não `[]` aqui. Um literal novo a
  // cada render faz os três `useMemo` abaixo recalcularem SEMPRE enquanto a
  // consulta não volta — a dependência muda de identidade sem mudar de
  // conteúdo. Apontado na primeira passada do linter, em 31/10/2026.
  const rows = list.data?.data ?? SEM_LINHAS;

  // ✅ DÍVIDA PAGA EM 31/10/2026 — e o conserto foi MUDAR DE LUGAR, não reescrever.
  //
  // O acumulado do gráfico era somado numa variável `let` dentro do `map`, aqui
  // no render. O compilador do React recusa isso (`Cannot reassign variable
  // after render completes`) e, ao desistir do componente, **para de preservar
  // a memoização manual do resto** — os dois `useMemo` abaixo caíam junto. Eram
  // os 4 avisos que ficaram silenciados por uma passada.
  //
  // 🔑 A conta não foi reescrita: saiu linha por linha para
  // `lib/financasSerie.ts`, onde o `let` é detalhe interno de uma função pura.
  // Reescrever a acumulação às cegas trocaria aviso de linter por erro de
  // saldo, e saldo errado numa tela de finanças não se percebe olhando.
  // 🧪 `npm run conferir:balanco` roda a implementação velha e a nova lado a
  // lado e exige igualdade em centavos.

  // Sumário derivado pros 4 cards do topo
  const summary = useMemo(() => computeSummary(rows), [rows]);
  const balance = useMemo(() => computeBalance(rows, balancePeriod), [rows, balancePeriod]);

  // Série pro gráfico (ordem ascendente pra trajetória cronológica + balanço
  // acumulado). Linha "Caixa" usa PROJETADO (não snapshot manual) pra mostrar
  // cascade real — meses não confirmados aparecem com valor calculado desde a
  // última âncora.
  const chartData = useMemo(() => construirSerieDoBalanco(rows), [rows]);

  const currentMonthExists = rows.some((r) => r.month.startsWith(currentMonthFirstDay().slice(0, 7)));

  return (
    <Layout
      title="Finanças"
      actions={
        !form && (
          <Button onClick={startAddMonth}>
            <Plus size={14} /> Adicionar mês
          </Button>
        )
      }
    >
      <p className="mb-6 text-sm text-text-muted">
        Snapshot mensal de <span className="text-text-primary">caixa</span> e <span className="text-text-primary">custos de infra</span> que alimentam <span className="text-brand">burn rate</span> e <span className="text-brand">runway</span> do dashboard de Monetização.
      </p>

      {/* === Cards de resumo === */}
      {rows.length > 0 && (
        <section className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
          <SummaryCard
            title="Caixa atual"
            value={BRL(summary.cashCents)}
            subtitle={summary.lastMonth ? `fim de ${monthLabel(summary.lastMonth)}` : '—'}
            icon={<Banknote size={18} className="text-brand" />}
            warning={summary.cashCents === 0}
          />
          <SummaryCard
            title="Burn rate (3m)"
            value={BRL(summary.burnAvg3mCents)}
            subtitle={summary.monthsCounted >= 3 ? 'média últimos 3 meses' : `média ${summary.monthsCounted} ${summary.monthsCounted === 1 ? 'mês' : 'meses'} disponíveis`}
            icon={<Flame size={18} className="text-brand" />}
          />
          <SummaryCard
            title="Runway"
            value={summary.runwayMonths != null
              ? `${summary.runwayMonths.toFixed(1)} ${summary.runwayMonths === 1 ? 'mês' : 'meses'}`
              : '—'
            }
            subtitle={summary.runwayMonths != null
              ? `caixa ÷ burn médio`
              : (summary.cashCents === 0 ? 'sem caixa' : 'sem burn calculado')
            }
            icon={<Clock size={18} className="text-brand" />}
            warning={summary.runwayMonths != null && summary.runwayMonths < 3}
          />
          <SummaryCard
            title="Tendência custo"
            value={summary.trendPct != null
              ? `${summary.trendPct > 0 ? '+' : ''}${summary.trendPct.toFixed(1)}%`
              : '—'
            }
            subtitle="vs mês anterior"
            icon={
              summary.trendPct == null
                ? <Minus size={18} className="text-text-muted" />
                : summary.trendPct > 0
                  ? <TrendingUp size={18} className="text-red-400" />
                  : summary.trendPct < 0
                    ? <TrendingDown size={18} className="text-green-400" />
                    : <Minus size={18} className="text-text-muted" />
            }
            valueClassName={
              summary.trendPct == null ? '' :
              summary.trendPct > 0 ? 'text-red-400' :
              summary.trendPct < 0 ? 'text-green-400' :
              ''
            }
          />
          <BalanceCard
            balance={balance}
            period={balancePeriod}
            onChangePeriod={setBalancePeriod}
          />
        </section>
      )}

      {/* === Aviso mês atual === */}
      {!currentMonthExists && !form && (
        <Card className="mb-4 border-brand-border bg-brand-bg">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="mt-0.5 flex-shrink-0 text-brand" />
            <div className="text-sm">
              <div className="font-medium text-text-primary">Mês atual ainda não registrado.</div>
              <div className="mt-1 text-text-secondary">
                Clica em "Adicionar mês" pra criar a row de <strong className="text-text-primary">{monthLabel(currentMonthFirstDay())}</strong>.
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* === Formulário (modo "adicionar mês" — aparece no topo) === */}
      {form && editingRowId === null && renderForm()}
      {/*
        Quando estamos editando uma row existente (`editingRowId !== null`),
        o form é renderizado dentro da própria row expandida na tabela,
        para evitar que o usuário precise scrollar até o topo. Ver bloco
        `isExpanded` mais abaixo.
      */}

      {/* === Gráfico === */}
      {chartData.length > 0 && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle>Trajetória financeira (últimos {chartData.length} {chartData.length === 1 ? 'mês' : 'meses'})</CardTitle>
            <span className="text-[10px] text-text-muted">clique nos itens da legenda pra mostrar/ocultar</span>
          </CardHeader>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 24, bottom: 0, left: 8 }}>
                <CartesianGrid stroke="#2A2A2A" strokeDasharray="3 3" />
                <XAxis dataKey="month" stroke="#737373" fontSize={12} />
                <YAxis
                  stroke="#737373"
                  fontSize={12}
                  width={70}
                  tickFormatter={(v) => BRL_SHORT(v * 100)}
                />
                <Tooltip {...DARK_TOOLTIP} formatter={(v: number) => BRL(Math.round(v * 100))} />
                <Legend wrapperStyle={{ fontSize: 12, color: '#A3A3A3' }} />
                <Line type="monotone" dataKey="caixa"   name="Caixa"              stroke="#A855F7" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="custo"   name="Custo total"        stroke="#FB923C" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="receita" name="Receita líquida"    stroke="#22C55E" strokeWidth={2} dot={{ r: 3 }} />
                {/* Tracejada e em azul de propósito: aporte não é faturamento.
                    Mesma cor do total aportado no formulário. */}
                <Line type="monotone" dataKey="aporte"  name="Aporte de sócio"    stroke="#38BDF8" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="2 3" />
                <Line type="monotone" dataKey="balanco" name="Balanço acumulado"  stroke="#38BDF8" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="4 2" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* === Tabela === */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico ({formatNumber(rows.length)} {rows.length === 1 ? 'mês' : 'meses'})</CardTitle>
        </CardHeader>

        {rows.length === 0 ? (
          <div className="py-8 text-center text-sm text-text-muted">
            Sem dados ainda. Adicione o primeiro mês acima pra começar a alimentar burn rate + runway.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs font-medium uppercase tracking-wide text-text-muted">
                  <th className="py-2 pr-2 w-6"></th>
                  <th className="py-2 pr-3">Mês</th>
                  <th className="py-2 pr-3 text-right">Caixa</th>
                  {/* A tabela NUNCA teve coluna de receita — só custo e caixa.
                      O gráfico desenhava a linha, mas R$ 12,22 numa escala de
                      R$ 6.000 é invisível, então na prática não havia onde LER
                      o número. Foi o que fez o Pablo procurar onde lançar os
                      R$ 12,22 da Apple e não achar nada. */}
                  <th className="py-2 pr-3 text-right">Receita</th>
                  <th className="py-2 pr-3 text-right">Total</th>
                  <th className="py-2 pr-3 text-right">Railway</th>
                  <th className="py-2 pr-3 text-right">R2</th>
                  <th className="py-2 pr-3 text-right">Resend</th>
                  <th className="py-2 pr-3 text-right">PostHog</th>
                  <th className="py-2 pr-3 text-right">Outros</th>
                  <th className="py-2 pl-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const isLatest      = idx === 0;
                  const hasBreakdown  = r.other_breakdown && r.other_breakdown.length > 0;
                  const hasNotes      = (r.notes_breakdown && r.notes_breakdown.length > 0) || !!r.notes;
                  const isExpanded    = expandedId === rowKey(r);
                  const isEditingHere = editingRowId === rowKey(r);
                  const canExpand     = hasBreakdown || hasNotes || isEditingHere;

                  return (
                    <>
                      <tr
                        key={rowKey(r)}
                        className={[
                          'border-b border-border',
                          isLatest ? 'bg-brand-bg/30' : '',
                          canExpand ? 'cursor-pointer hover:bg-bg-input/40' : '',
                        ].join(' ')}
                        onClick={() => canExpand && setExpandedId(isExpanded ? null : rowKey(r))}
                      >
                        <td className="py-3 pr-2 text-text-muted">
                          {canExpand
                            ? (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)
                            : null}
                        </td>
                        <td className="py-3 pr-3 font-medium text-text-primary">
                          {monthLabel(r.month.slice(0, 10))}
                          {isLatest && <span className="ml-2 rounded bg-brand-bg px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-brand">atual</span>}
                        </td>
                        <td className="py-3 pr-3 text-right">
                          <CashCell row={r} />
                        </td>
                        <td className="py-3 pr-3 text-right">
                          <RevenueCell row={r} />
                        </td>
                        <td className="py-3 pr-3 text-right font-semibold text-brand">{BRL(r.total_cost_cents)}</td>
                        <td className="py-3 pr-3 text-right text-text-secondary">{BRL(r.railway_cost_cents)}</td>
                        <td className="py-3 pr-3 text-right text-text-secondary">{BRL(r.r2_cost_cents)}</td>
                        <td className="py-3 pr-3 text-right text-text-secondary">{BRL(r.resend_cost_cents)}</td>
                        <td className="py-3 pr-3 text-right text-text-secondary">{BRL(r.posthog_cost_cents)}</td>
                        <td className="py-3 pr-3 text-right text-text-secondary">
                          {BRL(r.other_cost_cents)}
                          {hasBreakdown && (
                            <span className="ml-1 text-[10px] text-text-muted">({r.other_breakdown.length})</span>
                          )}
                        </td>
                        <td className="py-3 pl-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => startEditRow(r)}
                            className="text-text-secondary hover:text-brand"
                            title="Editar"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => {
                              if (confirm(`Remover ${monthLabel(r.month.slice(0, 10))}?`)) {
                                remove.mutate(r.month.slice(0, 10));
                              }
                            }}
                            className="ml-3 text-text-secondary hover:text-red-400"
                            title="Remover"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>

                      {isExpanded && canExpand && (
                        <tr key={`${rowKey(r)}-expanded`} className="border-b border-border bg-bg-input/40">
                          <td colSpan={10} className="py-3 pr-3 pl-10">
                            {/* Form inline (Mudança 2): renderiza acima do detalhamento quando editando esta row */}
                            {isEditingHere && form && (
                              <div className="mb-3">
                                {renderForm()}
                              </div>
                            )}
                            {hasBreakdown && (
                              <div className="mb-2">
                                <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wide text-text-muted">
                                  <span>Detalhamento de "Outros" ({r.other_breakdown.length} {r.other_breakdown.length === 1 ? 'item' : 'itens'})</span>
                                  <span className="normal-case text-[10px] text-text-muted">· clique num item pra ver detalhes</span>
                                </div>
                                <table className="w-full text-xs">
                                  <tbody>
                                    {r.other_breakdown.map((it, i) => {
                                      const meta       = it.category ? CATEGORY_META[it.category] : null;
                                      const hasExtras  = !!(it.vendor || it.category || it.source || it.invoice_ref || it.recurrence || it.next_charge);
                                      return (
                                        <tr
                                          key={i}
                                          className={[
                                            'border-b border-border/50 last:border-0',
                                            hasExtras ? 'cursor-pointer hover:bg-bg-input/60' : '',
                                          ].join(' ')}
                                          onClick={(e) => {
                                            if (!hasExtras) return;
                                            e.stopPropagation();  // evita colapsar a row expandida
                                            setDetailItem(it);
                                          }}
                                          title={hasExtras ? 'Clique pra ver detalhes' : undefined}
                                        >
                                          <td className="py-1.5 text-text-secondary">
                                            <div className="flex items-center gap-2">
                                              <span>{it.desc || <span className="italic text-text-muted">sem descrição</span>}</span>
                                              {meta && (
                                                <span
                                                  className={`inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide ${meta.badgeClass}`}
                                                  title={`Categoria: ${meta.label}`}
                                                >
                                                  {meta.label}
                                                </span>
                                              )}
                                              {it.vendor && (
                                                <span className="text-[10px] text-text-muted">· {it.vendor}</span>
                                              )}
                                              {hasExtras && (
                                                <Info size={11} className="text-text-muted opacity-60" />
                                              )}
                                            </div>
                                          </td>
                                          <td className="py-1.5 text-right text-text-primary">{BRL(it.cents)}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                            {((r.notes_breakdown && r.notes_breakdown.length > 0) || r.notes) && (
                              <div>
                                <div className="mb-1 text-xs uppercase tracking-wide text-text-muted">
                                  Notas
                                  {r.notes_breakdown && r.notes_breakdown.length > 0 && (
                                    <span className="ml-2 normal-case text-text-muted">
                                      ({r.notes_breakdown.length} {r.notes_breakdown.length === 1 ? 'item' : 'itens'})
                                    </span>
                                  )}
                                </div>
                                {r.notes_breakdown && r.notes_breakdown.length > 0 ? (
                                  <ul className="list-disc space-y-1 pl-5 text-sm text-text-secondary">
                                    {r.notes_breakdown.map((n, i) => (
                                      <li key={i}>{n}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <div className="text-sm text-text-secondary">{r.notes}</div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex items-center gap-2 text-xs text-text-muted">
          <Wallet size={14} className="text-brand" />
          <span>Os dados alimentam burn rate + runway em <strong className="text-text-primary">Monetização</strong>. Cache invalida automaticamente ao salvar.</span>
        </div>
      </Card>

      {/* Modal detalhe item other_breakdown (Monet 24/08). Read-only. */}
      {detailItem && (
        <OtherItemDetailModal item={detailItem} onClose={() => setDetailItem(null)} />
      )}
    </Layout>
  );
}

// ─── Sub-componentes ────────────────────────────────────────────────────────

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs text-text-secondary">
        {label}
        {hint && <span className="ml-2 text-text-muted">· {hint}</span>}
      </label>
      {children}
    </div>
  );
}

interface SummaryCardProps {
  title:           string;
  value:           string;
  subtitle:        string;
  icon:            React.ReactNode;
  warning?:        boolean;
  valueClassName?: string;
}

function SummaryCard({ title, value, subtitle, icon, warning, valueClassName }: SummaryCardProps) {
  return (
    <Card className={warning ? 'border-yellow-700/40' : ''}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardValue className={valueClassName || (warning ? 'text-yellow-400' : '')}>{value}</CardValue>
      <CardSubtitle>{subtitle}</CardSubtitle>
    </Card>
  );
}

// ─── CashCell: exibe caixa com contexto (confirmado vs projetado vs divergente) ─
//
// 3 casos:
//   1. confirmed_at != null e delta com projected ~= 0
//      → snapshot bate com projeção esperada. Mostra valor + ícone verde "confirmado".
//   2. confirmed_at != null e delta com projected != 0 (>R$1)
//      → snapshot manual diverge do calculado (fluxo não-tradicional: aporte, taxa
//        bancária, IOF, transferência entre contas). Mostra snapshot + delta amarelo
//        com tooltip explicando a divergência.
//   3. confirmed_at == null
//      → nunca confirmou este mês. Mostra projected + badge "estimado" cinza.
//
// Tolerância de R$1 (100 cents) — evita alerta falso por arredondamento
// entre BRL parseFloat e integer cents no backend.

const DELTA_TOLERANCE_CENTS = 100;

// Receita do mês, mostrando QUAL das duas fontes está valendo.
//
// As duas convivem por decisão do Monetização (09/09): o derivado vem das
// cobranças que o webhook registrou, o medido vem do relatório oficial da loja.
// O delta entre eles é a informação — foi um desvio assim que denunciou a
// estimativa errada do RevenueCat, que em 10/09 declarou 64,65% de repasse
// quando o real medido é 82,01%, uma diferença de 21% para menos.
//
// Por isso o desvio aparece na tela em vez de ficar só nos dados: número que
// ninguém olha não protege ninguém.
function RevenueCell({ row }: { row: FinanceRow }) {
  const medido   = row.revenue_net_cents;
  const derivado = row.revenue_cents ?? 0;
  const valor    = medido != null ? medido : derivado;
  // Medido pode vir de dois caminhos: você conferiu à mão, ou as cobranças do
  // mês já vieram do relatório oficial da loja. Os dois são medição.
  const ehMedido = medido != null || row.revenue_derived_is_measured;

  if (valor === 0) {
    return <span className="text-text-muted">—</span>;
  }

  // Só compara quando as duas fontes existem. Com uma só, não há desvio a
  // mostrar — e um "0%" ali sugeriria conferência que não aconteceu.
  const temAmbas = medido != null && derivado > 0;
  const desvioPct = temAmbas ? Math.round(((medido - derivado) / derivado) * 100) : 0;
  const desvioRelevante = temAmbas && Math.abs(desvioPct) >= 5;

  return (
    <div className="flex items-center justify-end gap-1.5">
      <span className="font-medium text-emerald-400">{BRL(valor)}</span>
      {ehMedido ? (
        <span
          className="rounded bg-emerald-950/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-emerald-300"
          title="Conferido no relatório da loja — medição, não estimativa"
        >
          medido
        </span>
      ) : (
        <span
          className="inline-flex items-center gap-0.5 rounded bg-bg-input px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-text-muted"
          title="Estimado a partir das cobranças registradas. O valor definitivo entra quando o relatório da loja fechar o período."
        >
          <Sparkles size={10} /> estimado
        </span>
      )}
      {desvioRelevante && (
        <span
          className="rounded bg-yellow-950/60 px-1.5 py-0.5 text-[10px] text-yellow-300"
          title={`O medido difere ${desvioPct}% do estimado pelas cobranças (${BRL(derivado)}). Vale entender de onde vem a diferença.`}
        >
          {desvioPct > 0 ? '+' : ''}{desvioPct}%
        </span>
      )}
    </div>
  );
}

function CashCell({ row }: { row: FinanceRow }) {
  const confirmed  = row.cash_balance_confirmed_at != null;
  const snapshot   = row.cash_balance_cents;
  const projected  = row.cash_balance_projected_cents;
  const delta      = snapshot - projected;
  const hasDelta   = confirmed && Math.abs(delta) > DELTA_TOLERANCE_CENTS;
  const isZero     = confirmed ? snapshot === 0 : projected === 0;

  if (!confirmed) {
    // Caso 3: estimado via cascade
    return (
      <div className="flex items-center justify-end gap-1.5">
        <span className={isZero ? 'text-yellow-400' : 'text-text-secondary'}>
          {BRL(projected)}
        </span>
        <span
          className="inline-flex items-center gap-0.5 rounded bg-bg-input px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-text-muted"
          title="Não confirmado com extrato bancário — valor calculado desde último mês confirmado somando receita e subtraindo custos"
        >
          <Sparkles size={10} /> estimado
        </span>
      </div>
    );
  }

  // Casos 1 e 2: confirmado
  const confirmedAt = new Date(row.cash_balance_confirmed_at!);
  const confirmedLabel = confirmedAt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return (
    <div className="flex items-center justify-end gap-1.5">
      <span className={isZero ? 'text-yellow-400' : 'text-text-primary'}>
        {BRL(snapshot)}
      </span>
      {hasDelta ? (
        <span
          className="inline-flex items-center gap-0.5 rounded bg-yellow-950/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-yellow-400"
          title={`Snapshot difere da projeção em ${BRL(Math.abs(delta))} (${delta > 0 ? 'sobra' : 'faltou'}). Provável causa: aporte pessoal, taxa bancária, IOF, ou transferência entre contas não registrada em receita/custos.`}
        >
          {delta > 0 ? '+' : '-'}{BRL(Math.abs(delta))} vs projeção
        </span>
      ) : (
        <span title={`Confirmado com extrato em ${confirmedLabel}`} className="inline-flex">
          <BadgeCheck size={12} className="text-green-400" />
        </span>
      )}
    </div>
  );
}

// ─── Card de Balanço (receita - custo) com seleção de período ────────────────

const PERIOD_LABELS: Record<BalancePeriod, string> = {
  current_month: 'Mês atual',
  last_3m:       'Últimos 3 meses',
  last_12m:      'Últimos 12 meses',
  ytd:           'Ano atual (YTD)',
  all:           'Todo histórico',
};

interface BalanceCardProps {
  balance:        BalanceCalc;
  period:         BalancePeriod;
  onChangePeriod: (p: BalancePeriod) => void;
}

function BalanceCard({ balance, period, onChangePeriod }: BalanceCardProps) {
  const isPositive = balance.balanceCents > 0;
  const isNegative = balance.balanceCents < 0;
  const valueColor = isPositive ? 'text-green-400' : isNegative ? 'text-red-400' : 'text-text-secondary';
  const sign       = balance.balanceCents > 0 ? '+' : '';

  return (
    <Card className={isNegative ? 'border-red-900/40' : isPositive ? 'border-green-900/40' : ''}>
      <CardHeader>
        <CardTitle>Balanço</CardTitle>
        <Scale size={18} className={isPositive ? 'text-green-400' : isNegative ? 'text-red-400' : 'text-brand'} />
      </CardHeader>
      <CardValue className={valueColor}>{sign}{BRL(balance.balanceCents)}</CardValue>
      <CardSubtitle>
        {balance.rangeLabel}
        {balance.comparisonPct != null && (
          <span className={`ml-2 text-[11px] ${balance.comparisonPct > 0 ? 'text-green-400' : balance.comparisonPct < 0 ? 'text-red-400' : 'text-text-muted'}`}>
            {balance.comparisonPct > 0 ? '+' : ''}{balance.comparisonPct.toFixed(1)}% vs ant.
          </span>
        )}
      </CardSubtitle>

      <select
        value={period}
        onChange={(e) => onChangePeriod(e.target.value as BalancePeriod)}
        className="mt-3 w-full rounded border border-border bg-bg-input px-2 py-1 text-xs text-text-secondary focus:border-brand focus:outline-none"
      >
        {(Object.keys(PERIOD_LABELS) as BalancePeriod[]).map((p) => (
          <option key={p} value={p}>{PERIOD_LABELS[p]}</option>
        ))}
      </select>
    </Card>
  );
}

// ─── Cálculo de sumário ──────────────────────────────────────────────────────

interface Summary {
  cashCents:       number;
  burnAvg3mCents:  number;
  runwayMonths:    number | null;
  trendPct:        number | null;     // % MoM do custo total (positivo = subindo)
  lastMonth:       string | null;
  monthsCounted:   number;
}

function computeSummary(rows: FinanceRow[]): Summary {
  if (rows.length === 0) {
    return { cashCents: 0, burnAvg3mCents: 0, runwayMonths: null, trendPct: null, lastMonth: null, monthsCounted: 0 };
  }

  // rows vem DESC (mais recente primeiro).
  // cashCents usa PROJETADO do mês mais recente pra refletir realidade financeira
  // (cascade desde último confirmado). Se nenhum mês foi confirmado, projected = 0
  // e o card mostra warning. Ver [[admin.finances.controller]] `list` cascade JS.
  const latest          = rows[0];
  const cashCents       = latest.cash_balance_projected_cents;
  const lastMonth       = latest.month.slice(0, 10);
  const lastN           = rows.slice(0, 3);
  const monthsCounted   = lastN.length;
  const burnAvg3mCents  = lastN.length > 0
    ? Math.round(lastN.reduce((acc, r) => acc + r.total_cost_cents, 0) / lastN.length)
    : 0;
  const runwayMonths    = burnAvg3mCents > 0 ? cashCents / burnAvg3mCents : null;
  const trendPct        = rows.length >= 2 && rows[1].total_cost_cents > 0
    ? ((latest.total_cost_cents - rows[1].total_cost_cents) / rows[1].total_cost_cents) * 100
    : null;

  return { cashCents, burnAvg3mCents, runwayMonths, trendPct, lastMonth, monthsCounted };
}

// ─── Cálculo de balanço (receita - custo) por período ────────────────────────
//
// Operacionalização:
// - rows vem DESC (mais recente primeiro)
// - Filtra rows cujo mês cai no período selecionado
// - Soma revenue_cents - total_cost_cents
// - Comparação = mesmo balanço aplicado ao período anterior equivalente
//   (ex: "Últimos 3m" → compara com 3 meses antes deles)
// - YTD compara com YTD do ano anterior (mesma janela)

function periodRange(period: BalancePeriod, now: Date): { start: Date; end: Date; label: string } {
  const y  = now.getFullYear();
  const m  = now.getMonth(); // 0-11
  const monthNames = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  const start = new Date(y, m, 1);
  const end   = new Date(y, m + 1, 0); // último dia do mês atual

  switch (period) {
    case 'current_month':
      return { start, end, label: `${monthNames[m]}/${y}` };
    case 'last_3m':
      return { start: new Date(y, m - 2, 1), end, label: 'últimos 3 meses' };
    case 'last_12m':
      return { start: new Date(y, m - 11, 1), end, label: 'últimos 12 meses' };
    case 'ytd':
      return { start: new Date(y, 0, 1), end, label: `${y} (YTD)` };
    case 'all':
      return { start: new Date(1970, 0, 1), end: new Date(2100, 0, 1), label: 'todo histórico' };
  }
}

function previousRange(period: BalancePeriod, current: { start: Date; end: Date }): { start: Date; end: Date } | null {
  if (period === 'all') return null;
  const ms      = current.end.getTime() - current.start.getTime();
  const prevEnd = new Date(current.start.getTime() - 1);                 // dia anterior ao início atual
  const prevSt  = new Date(prevEnd.getTime() - ms);
  return { start: prevSt, end: prevEnd };
}

function rowInRange(r: FinanceRow, range: { start: Date; end: Date }): boolean {
  const monthDate = new Date(r.month.slice(0, 10) + 'T00:00:00');
  return monthDate >= range.start && monthDate <= range.end;
}

function sumBalanceInRange(rows: FinanceRow[], range: { start: Date; end: Date }) {
  let revenue = 0;
  let cost    = 0;
  let count   = 0;
  for (const r of rows) {
    if (!rowInRange(r, range)) continue;
    // Mesma escolha do gráfico e da projeção de caixa: o medido quando existe.
    // E aporte NÃO entra aqui — este balanço é operacional, e somar dinheiro do
    // próprio Pablo faria um mês de injeção parecer um mês de resultado.
    revenue += r.revenue_effective_cents ?? r.revenue_cents ?? 0;
    cost    += r.total_cost_cents;
    count   += 1;
  }
  return { revenue, cost, balance: revenue - cost, count };
}

function computeBalance(rows: FinanceRow[], period: BalancePeriod): BalanceCalc {
  if (rows.length === 0) {
    return { revenueCents: 0, costCents: 0, balanceCents: 0, comparisonPct: null, rangeLabel: PERIOD_LABELS[period].toLowerCase(), monthsIncluded: 0 };
  }

  const now     = new Date();
  const range   = periodRange(period, now);
  const current = sumBalanceInRange(rows, range);

  const prevRange = previousRange(period, range);
  let comparisonPct: number | null = null;
  if (prevRange) {
    const prev = sumBalanceInRange(rows, prevRange);
    if (prev.count > 0 && Math.abs(prev.balance) > 0) {
      // % mudança no balanço: positivo = melhorou (mais lucro / menos prejuízo)
      comparisonPct = ((current.balance - prev.balance) / Math.abs(prev.balance)) * 100;
    }
  }

  return {
    revenueCents:   current.revenue,
    costCents:      current.cost,
    balanceCents:   current.balance,
    comparisonPct,
    rangeLabel:     range.label,
    monthsIncluded: current.count,
  };
}

// ─── Categoria + Recurrence (padrão B+ Monet 18/08) ─────────────────────────
//
// CATEGORY_META: label pt-BR + classe Tailwind pro badge. Cores escolhidas
// pra criar contraste visual entre categorias no scan rápido da tabela.
// Todas usam paleta bg-950 (fundo escuro) + text-300 (contraste WCAG AA)
// pra bater com tema dark do admin.

const CATEGORY_META: Record<Category, { label: string; badgeClass: string }> = {
  operacional:    { label: 'operacional',    badgeClass: 'bg-zinc-800 text-zinc-300' },
  infra:          { label: 'infra',          badgeClass: 'bg-blue-950 text-blue-300' },
  juridico:       { label: 'jurídico',       badgeClass: 'bg-purple-950 text-purple-300' },
  marketing:      { label: 'marketing',      badgeClass: 'bg-emerald-950 text-emerald-300' },
  contabilidade:  { label: 'contabilidade',  badgeClass: 'bg-amber-950 text-amber-300' },
  equipamento:    { label: 'equipamento',    badgeClass: 'bg-orange-950 text-orange-300' },
  outros:         { label: 'outros',         badgeClass: 'bg-neutral-800 text-neutral-400' },
};

const RECURRENCE_LABEL: Record<Recurrence, string> = {
  mensal:      'mensal',
  bimestral:   'bimestral',
  trimestral:  'trimestral',
  semestral:   'semestral',
  anual:       'anual',
  bienal:      'bienal',
  unico:       'único',
};

// ─── Modal detalhe item other_breakdown ──────────────────────────────────────
//
// Read-only por enquanto (sprint mínima Monet 24/08). Editor rico é sprint
// desejável opcional. Compat retroativa: mostra APENAS campos presentes,
// sem "N/A" pra faltantes (evita ruído em rows antigas com só desc+cents —
// nesse caso o item nem fica clicável, então esse modal nunca abre pra ele).

// Linha do modal de detalhe. ⚠️ FORA do componente, e isso não é estilo:
// declarada dentro, ela é um componente NOVO a cada render — o React desmonta
// e remonta a subárvore, perdendo estado e foco. Apontado pelo linter que o
// painel ganhou em 31/10/2026 (`react-hooks/static-components`), na primeira
// passada. Aqui o `Row` não tem estado próprio, então o sintoma era invisível;
// o dia em que tivesse um campo de edição dentro, não seria.
function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border/50 py-2 last:border-0">
      <span className="text-[10px] uppercase tracking-wide text-text-muted">{label}</span>
      <span className="text-sm text-text-primary">{value}</span>
    </div>
  );
}

function OtherItemDetailModal({ item, onClose }: { item: OtherItem; onClose: () => void }) {
  const meta = item.category ? CATEGORY_META[item.category] : null;

  const nextChargeLabel = item.next_charge
    ? (() => {
        const [y, m] = item.next_charge.split('-');
        const names = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
        return `${names[parseInt(m, 10) - 1]}/${y}`;
      })()
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-xl border border-border bg-bg-elevated shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-border px-5 py-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Info size={14} className="flex-shrink-0 text-brand" />
              <div className="text-xs uppercase text-text-muted">Detalhe do item</div>
            </div>
            <div className="mt-1 truncate text-lg font-semibold text-text-primary">
              {item.desc || <span className="italic text-text-muted">sem descrição</span>}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-xl font-bold text-brand">{BRL(item.cents)}</span>
              {meta && (
                <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${meta.badgeClass}`}>
                  {meta.label}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-text-muted hover:bg-bg-input hover:text-text-primary"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-5 py-3">
          {item.vendor      && <Row label="Fornecedor / Vendor"  value={item.vendor} />}
          {item.source      && <Row label="Fonte / Source"       value={item.source} />}
          {item.invoice_ref && <Row label="Referência de fatura" value={item.invoice_ref} />}
          {item.recurrence  && (
            <Row
              label="Recorrência"
              value={
                <span>
                  {RECURRENCE_LABEL[item.recurrence]}
                  {nextChargeLabel && item.recurrence !== 'unico' && (
                    <span className="ml-2 text-xs text-text-muted">· próxima: {nextChargeLabel}</span>
                  )}
                </span>
              }
            />
          )}
          {/* next_charge sozinho (sem recurrence) — raro mas suportado */}
          {nextChargeLabel && !item.recurrence && (
            <Row label="Próxima cobrança" value={nextChargeLabel} />
          )}
        </div>

        <div className="border-t border-border bg-bg-primary px-5 py-3 text-xs text-text-muted">
          Edição direto na UI é sprint futura. Por ora, edite via SQL Railway ou pelo formulário do mês.
        </div>
      </div>
    </div>
  );
}

// ─── Tooltip do gráfico ──────────────────────────────────────────────────────

const DARK_TOOLTIP = {
  contentStyle: {
    background: '#1A1A1A',
    border: '1px solid #2A2A2A',
    borderRadius: 8,
    fontSize: 12,
  },
  labelStyle: { color: '#F5F5F5' },
  itemStyle:  { color: '#A3A3A3' },
};
